<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import InputCheckbox from '../../../../../../../resources/js/components/umisis/form/inputs/InputCheckbox.vue'
import InputText from '../../../../../../../resources/js/components/umisis/form/inputs/InputText.vue'
import { useApiAction } from '../../../../../../../resources/js/composables/dispatchers/useApiAction.js'
import { useBusinessSystemStore } from '../../stores/businessSystems.js'

const props = defineProps({
  businessSystem: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['updated'])
const store = useBusinessSystemStore()
const { componentRef: dialog, openModal, closeModal } = useModalControl()

const systemName = ref('')
const systemDescription = ref('')
const businessSystemIsPublic = ref(false)

const businessSystemId = computed(() => {
  const parsedId = Number(props.businessSystem?.id)

  if (!Number.isInteger(parsedId) || parsedId <= 0) {
    return null
  }

  return parsedId
})

const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
const fieldControlClass = (fieldName) => ([
  fieldControlBaseClass,
  validationResult(fieldName) === false ? 'border-destructive focus-visible:ring-destructive bg-red-50' : '',
  validationResult(fieldName) === true ? 'border-success focus-visible:ring-success bg-green-50' : '',
])

const setDefaults = () => {
  systemName.value = props.businessSystem?.name ?? ''
  systemDescription.value = props.businessSystem?.objective ?? ''
  businessSystemIsPublic.value = props.businessSystem?.public === true
}

const onShow = () => {
  setDefaults()
}

const { execute, loading, getError, validationResult } = useApiAction((payload) => {
  if (businessSystemId.value === null) {
    return null
  }

  return store.updateBusinessSystemBasics(businessSystemId.value, payload)
}, {
  onSuccess: (businessSystem) => {
    if (!businessSystem) {
      return
    }

    toast.success('Business system updated.')
    emit('updated', businessSystem)
    closeModal()
  },
})

defineExpose({
  openModal,
  closeModal,
})

const handleOk = async (bvEvent) => {
  if (bvEvent) {
    bvEvent.preventDefault()
  }

  await execute({
    name: systemName.value,
    objective: systemDescription.value || null,
    public: businessSystemIsPublic.value,
  })
}
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Edit Business System"
    description="Update the system name, description, and visibility."
    ok-title="Save changes"
    cancel-title="Cancel"
    :ok-disabled="loading || businessSystemId === null"
    @ok="handleOk"
    @show="onShow"
  >
    <div class="flex flex-col gap-4">
      <InputText
        v-model="systemName"
        label="Name"
        placeholder="Customer Onboarding"
        :validation="validationResult('name')"
        :error-message="getError('name')"
      />

      <Field>
        <FieldLabel for="business-system-description">
          Description
        </FieldLabel>
        <textarea
          id="business-system-description"
          v-model="systemDescription"
          rows="3"
          placeholder="Describe the purpose of this business system"
          :class="fieldControlClass('objective')"
        />
        <FieldDescription v-if="getError('objective')" class="text-destructive font-medium">
          {{ getError('objective') }}
        </FieldDescription>
      </Field>

      <InputCheckbox
        v-model="businessSystemIsPublic"
        label="Publicly visible"
        :validation="validationResult('public')"
        :error-message="getError('public')"
      />
    </div>
  </UmisisModal>
</template>
